import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Checkbox, FormControlLabel, Typography, Grid, Link as MuiLink , Alert } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../stores/store';
import { saveUserDetails } from '../../features/games/bicycleGameSlice';
import { generateUserId } from '../../features/games/bicycleGameSlice';
import joc from '../../assets/images/bicycle_game/preview-joc.png'
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import theme from '../../styles/themes/default';

interface BicycleGameFormProps {
  onFormSuccess?: (userId: string) => void;
  onStartGame?: () => void;
}

const BicycleGameForm: React.FC<BicycleGameFormProps> = ({ onFormSuccess, onStartGame }) => {
  const dispatch = useAppDispatch();
  const { user, error: apiError } = useAppSelector((state) => state.bicycleGame);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    birthDate: null as Dayjs | null,
    phoneNumber: '',
    agree: false,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // Funcție pentru validarea formularului
  const validateFields = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.birthDate) newErrors.birthDate = 'Data nașterii este obligatorie';
    if (!formData.lastName) newErrors.lastName = 'Numele este obligatoriu';
    if (!formData.firstName) newErrors.firstName = 'Prenumele este obligatoriu';
    if (!formData.phoneNumber) newErrors.phoneNumber = 'Numărul de telefon este obligatoriu';
    if (!formData.agree) newErrors.agree = 'Trebuie să acceptati regulamentul';
    
    // Validare număr de telefon
    if (formData.phoneNumber && formData.phoneNumber.length !== 10) {
      newErrors.phoneNumber = 'Numărul de telefon trebuie să fie de 10 cifre';
    }
    
    // Validare vârstă minimă
    if (formData.birthDate) {
      const birthDate = formData.birthDate.toDate();
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (age < 18 || (age === 18 && monthDiff < 0) || (age === 18 && monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        newErrors.birthDate = 'Trebuie să aveti cel putin 18 ani';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

   // Funcție comună pentru a obține sau genera un ID
   const getOrGenerateUserId = async (): Promise<string> => {
    // Verificăm mai întâi în localStorage
    const storedUserId = localStorage.getItem('bicycleGameUserId');
    if (storedUserId) {
      return storedUserId;
    }
    
    if (user) {
      // Salvăm ID-ul din Redux și în localStorage pentru consistență
      localStorage.setItem('bicycleGameUserId', user);
      return user;
    }
    
    // Dacă nu există nicăieri, generăm unul nou
    try {
      const newUserId = await dispatch(generateUserId()).unwrap();
      if (!newUserId) {
        throw new Error('Could not generate user ID');
      }
      
      // Salvăm noul ID în localStorage
      localStorage.setItem('bicycleGameUserId', newUserId);
      return newUserId;
    } catch (error) {
      console.error('Error generating user ID:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitSuccess(false);
    setSubmitError(null);
    
    if (!validateFields()) {
      setSubmitError('Vă rugăm să completati toate câmpurile obligatorii corect.');
      return;
    }
    
    try {
      const userId = await getOrGenerateUserId();
      
      const result = await dispatch(saveUserDetails({
        unique_id: userId,
        firstName: formData.firstName || null,
        lastName: formData.lastName || null,
        birthDate: formData.birthDate ? formData.birthDate.format('YYYY-MM-DD') : null,
        phoneNumber: formData.phoneNumber || null
      })).unwrap();

      setSubmitSuccess(true);

      // Delay to show success message before proceeding
      setTimeout(() => {
        if (onStartGame) {
          onStartGame();
        } else if (onFormSuccess) {
          onFormSuccess(userId);
        }
      }, 5000);
      
    } catch (error: any) {
      console.error('Error saving user details:', error);
      
      // Verificăm dacă eroarea vine cu un mesaj specific din API
      if (error && typeof error === 'object') {
        // Dacă există un mesaj de eroare în răspunsul API
        if (error.message) {
          setSubmitError(error.message);
        } 
        // Dacă există erori de validare pentru câmpuri specifice
        else if (error.errors && typeof error.errors === 'object') {
          const fieldErrors: { [key: string]: string } = {};
          Object.entries(error.errors).forEach(([key, value]) => {
            fieldErrors[key.toLowerCase()] = Array.isArray(value) ? value[0] : String(value);
          });
          setErrors(prev => ({ ...prev, ...fieldErrors }));
          setSubmitError('Există erori de validare. Vă rugăm să verificati formatul datelor introduse.');
        } 
        // Pentru alte tipuri de erori
        else {
          setSubmitError('A apărut o eroare la salvarea datelor. Vă rugăm să încercati din nou.');
        }
      } else {
        setSubmitError('A apărut o eroare la salvarea datelor. Vă rugăm să încercati din nou.');
      }
    }
  };
  
  const handleGameStart = async () => {
    setSubmitError(null);
    try {
      const userId = await getOrGenerateUserId();
      
      if (onStartGame) {
        onStartGame();
      } else if (onFormSuccess) {
        onFormSuccess(userId);
      }
    } catch (error) {
      console.error('Error starting game:', error);
    }
  };

  const handleBirthdateChange = (newDate: Dayjs | null) => {
    setFormData(prev => ({ ...prev, birthDate: newDate }));
    setIsDatePickerOpen(false);
  };

  const handleBirthdateFocus = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDatePickerOpen(true);
  };


  return (
    <Box sx={{
      width: '100%',
      margin: '0 auto',
      padding: { xs: '2rem', sm: '2.5rem', md: '3rem' },
      backgroundColor: '#4a3824',
      overflow: 'hidden',
      boxSizing: 'border-box',
      maxWidth: '100%',
      display: 'flex',
      justifyContent: 'center',
    }}>
      <Grid container spacing={0} sx={{
        border: '4px dotted #e9d4a8',
        width: '100%',
        margin: 0,
        padding: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
        boxSizing: 'border-box',
        maxWidth: '1400px',
      }}>
        {/* Left Side - Form */}
        <Grid item xs={12} md={6} sx={{  padding: { xs: '1rem', sm: '1.5rem' },
        boxSizing: 'border-box',}}>
          <Box component="form" onSubmit={handleSubmit} sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 2,
          height: '100%',
        }}>
            <Typography variant="h4" sx={{ 
              textAlign: 'center', 
              color: '#e9d4a8',
              mb: 1,
              fontFamily: theme.fonts.poplar 
            }}>
             ALĂTURĂ-TE ȘI TU
            </Typography>
            
            <Typography variant="h6" sx={{ 
              textAlign: 'center', 
              color: '#e9d4a8',
              mb: 1 
            }}>
              Completează formularul și ești înscris în tragerea la sorți pentru acordarea bicicletelor electrice
            </Typography>
            
            <Typography sx={{ 
              textAlign: 'center', 
              color: '#e9d4a8',
              mb: 2
            }}>
              *după completarea formularului, îti vom afișa jocul
            </Typography>

            {submitSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Felicitări! Datele tale au fost înregistrate cu succes.
              </Alert>
            )}
            
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitError}
              </Alert>
            )}

          <DatePicker
              label="Data nașterii*"
              value={formData.birthDate}
              onAccept={handleBirthdateChange}
              format="DD/MM/YYYY"
              open={isDatePickerOpen}
              onOpen={() => setIsDatePickerOpen(true)}
              onClose={() => setIsDatePickerOpen(false)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.birthDate,
                  helperText: errors.birthDate,
                  onClick: handleBirthdateFocus,
                  sx: {
                    backgroundColor: '#f5e8c9',
                    borderRadius: '4px',
                    '& .MuiInputBase-input': {
                      textAlign: 'center',
                      fontWeight: 'bold',
                      color: '#4a3824',
                      padding: '12px 14px',
                    },
                    '& .MuiInputLabel-root': {
                      color: '#4a3824',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      left: 0,
                      right: 0,
                      marginLeft: 'auto',
                      marginRight: 'auto',
                      width: 'fit-content',
                      transform: 'translate(0, 8px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        opacity: 0
                      }
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '& .MuiFormHelperText-root': {
                      color: '#ff1744',
                      margin: '4px 0 0',
                      fontSize: '12px',
                      textAlign: 'center'
                    }
                  }
                }
              }}
            />

            <TextField
              required
              placeholder="Nume"
              value={formData.lastName}
              onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
              error={!!errors.lastName}
              helperText={errors.lastName}
              sx={{
                backgroundColor: '#f5e8c9',
                borderRadius: '4px',
                '& .MuiInputBase-input': {
                  textAlign: 'center',
                  fontWeight: 'bold',
                  color: '#4a3824',
                  padding: '12px 14px',
                },
                '& input::placeholder': {
                  color: '#4a3824',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  opacity: 1
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                }
              }}
            />

            <TextField
              required
              placeholder="Prenume"
              value={formData.firstName}
              onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
              error={!!errors.firstName}
              helperText={errors.firstName}
              sx={{
                backgroundColor: '#f5e8c9',
                borderRadius: '4px',
                '& .MuiInputBase-input': {
                  textAlign: 'center',
                  fontWeight: 'bold',
                  color: '#4a3824',
                  padding: '12px 14px',
                },
                '& input::placeholder': {
                  color: '#4a3824',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  opacity: 1
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                }
              }}
            />

        <TextField
              required
              type="tel"
              placeholder="Număr de telefon"
              value={formData.phoneNumber}
              onChange={(e) => {
                // Only allow digits and limit to 10 characters
                const value = e.target.value.replace(/[^0-9]/g, '');
                if (value.length <= 10) {
                  setFormData(prev => ({ ...prev, phoneNumber: value }))
                }
              }}
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber}
              sx={{
                backgroundColor: '#f5e8c9',
                borderRadius: '4px',
                '& .MuiInputBase-input': {
                  textAlign: 'center',
                  fontWeight: 'bold',
                  color: '#4a3824',
                  padding: '12px 14px',
                },
                '& input::placeholder': {
                  color: '#4a3824',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  opacity: 1
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '& .MuiFormHelperText-root': {
                  color: '#ff1744',
                  margin: '4px 0 0',
                  fontSize: '12px',
                  textAlign: 'center'
                }
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.agree}
                  onChange={(e) => setFormData(prev => ({ ...prev, agree: e.target.checked }))}
                  required
                  sx={{
                    color: '#e9d4a8',
                    '&.Mui-checked': {
                      color: '#e9d4a8',
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ 
                  fontSize: '14px', 
                  lineHeight: 1.2, 
                  color: '#e9d4a8' 
                }}>
                  Am citit integral, am înteles și sunt de acord cu prevederile{' '}
                  <span style={{
                    fontStyle: 'italic',
                    textDecoration: 'underline',
                    fontWeight: 'bold',
                  }}>
                    <MuiLink 
                    href="/pdf/2025/Regulament autentificat_Kozel @ online.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: '#e9d4a8',
                      fontStyle: 'italic',
                      textDecoration: 'underline',
                      fontWeight: 'bold',
                      '&:hover': {
                        color: '#ffffff'
                      }
                    }}
                  >
                    Regulamentului Campaniei
                  </MuiLink>
                  </span>
                </Typography>
              }
              sx={{ 
                mb: 2,
                '& .MuiFormControlLabel-asterisk': {
                  display: 'none'
                }
              }}
            />
            {errors.agree && (
              <Typography color="error" sx={{ fontSize: '12px', textAlign: 'center', mt: -2, mb: 1 }}>
                {errors.agree}
              </Typography>
            )}

            <Button
              type="submit"
              sx={{
                backgroundColor: '#f5e8c9',
                color: '#4a3824',
                fontWeight: 'bold',
                padding: '10px 20px',
                width: '180px',
                mt: 2,
                alignSelf: 'center',
                fontSize: '1.2rem',
                '&:hover': {
                  backgroundColor: '#ffffff',
                },
                borderRadius: '4px',
                boxShadow: 'none',
                textTransform: 'uppercase'
              }}
            >
              Particip
            </Button>
          </Box>
        </Grid>

        {/* Right Side - Game */}
      <Grid item xs={12} md={6} sx={{ 
        padding: { xs: '1rem', sm: '1.5rem' },
        boxSizing: 'border-box',
      }}>
        <Typography variant="h4" sx={{ 
          textAlign: 'center', 
          color: '#e9d4a8',
          mb: 1 ,
          fontFamily: theme.fonts.poplar 
        }}>
          VREI DOAR SĂ PEDALEZI?
        </Typography>
        
        <Typography variant="h6" sx={{ 
          textAlign: 'center', 
          fontWeight: 'bold', 
          color: '#e9d4a8',
          mb: 1 
        }}>
          ACCESEAZĂ JOCUL MAI JOS.
        </Typography>
        
        <Typography sx={{ 
          textAlign: 'center', 
          color: '#e9d4a8',
          mb: 3
        }}>
          *kilometri vor fi donați cauzei, însă nu vei participa în tragerea la sorți
        </Typography>

        {/* Game Image Container */}
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3
        }}>
          {/* Game Image */}
          <Box 
            component="img"
            src={joc}
            alt="Joc Bicicleta"
            sx={{
              width: { xs: '100%', md: '85%' },
              height: 'auto',
              borderRadius: '8px',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              mb: 3
            }}
          />
    
    {/* Start Game Button - Positioned below the image */}
    <Button
      onClick={handleGameStart}
      sx={{
        backgroundColor: '#f5e8c9',
        color: '#4a3824',
        fontWeight: 'bold',
        padding: '10px 20px',
        width: '180px',
        fontSize: '1.2rem',
        '&:hover': {
          backgroundColor: '#ffffff',
        },
        borderRadius: '4px',
        boxShadow: 'none',
        textTransform: 'uppercase',
        mt: { xs: 2, sm: 2, md: 5 }
      }}
    >
      Start Joc
    </Button>
  </Box>
</Grid>
      </Grid>
    </Box>
  );
};

export default BicycleGameForm;